import { Helmet } from "react-helmet-async";

const SITE = "https://cphhighlights.com";
const DEFAULT_IMAGE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/6ebajbre_Nyhavn.webp";

// Freshness signals for structured data. datePublished is fixed; dateModified
// stamps the current build date so each rebuild tells crawlers the guide is
// kept up to date. Schema-only — no visible "Updated" line on the pages.
const DATE_PUBLISHED = "2026-07-01";
const DATE_MODIFIED = new Date().toISOString().slice(0, 10);

/**
 * Per-page SEO: unique title, description, canonical + Open Graph / Twitter.
 * `path` should start with "/" (e.g. "/areas"). Falls back to home defaults.
 * `preloadImage` (optional): URL of the LCP image to <link rel="preload"> for
 * faster first paint — pass the hero image on image-led landing pages only.
 */
export const Seo = ({ title, description, path = "/", image = DEFAULT_IMAGE, type = "website", noindex = false, preloadImage }) => {
  const url = `${SITE}${path}`;
  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />
      {preloadImage && <link rel="preload" as="image" href={preloadImage} fetchpriority="high" />}

      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

/**
 * JSON-LD structured data for a content page: WebPage + BreadcrumbList.
 * `crumb` = the page's label in the breadcrumb (omit on the homepage).
 */
export const PageSchema = ({ name, description, path = "/", crumb }) => {
  const url = `${SITE}${path}`;
  const webpage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    url,
    description,
    datePublished: DATE_PUBLISHED,
    dateModified: DATE_MODIFIED,
    isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
    publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      ...(crumb ? [{ "@type": "ListItem", position: 2, name: crumb, item: url }] : []),
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(webpage)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
    </Helmet>
  );
};

